import { memo, useCallback, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Cell, Row, flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Creator } from "@/types/types";
import { useCreatorsTableContext, useCreatorsTableState } from "./CreatorsTableContext";

interface CreatorsTableBodyProps {
  columnsLength: number;
}

// Rows are ~57px (h-11 buttons + p-1.5 cells); measured after mount anyway.
const ROW_HEIGHT_ESTIMATE = 57;
// Rows rendered beyond the viewport on each side — scroll headroom.
const OVERSCAN_ROWS = 8;
// First render happens before the scroll container is known: size the
// window like a typical pane instead of rendering only the overscan.
const INITIAL_RECT = { width: 0, height: 900 };

/**
 * One row, memoized on what can actually change it: the row (data), its
 * visible cells (columns / visibility), selection and the open-panel
 * stripe. Table UI state (search text, loading, dialogs) lives in a separate
 * context, so a keystroke re-renders the body's row list — not the rows.
 */
const TableRowView = memo(function TableRowView({
  row,
  cells,
  index,
  selected,
  panelSelected,
  measureElement,
}: {
  row: Row<Creator>;
  cells: Cell<Creator, unknown>[];
  index: number;
  selected: boolean;
  panelSelected: boolean;
  measureElement: (element: HTMLTableRowElement | null) => void;
}) {
  return (
    <TableRow
      ref={measureElement}
      data-index={index}
      data-creator-id={row.original._id}
      data-state={selected ? "selected" : undefined}
      className={cn(
        // Hover/selected backgrounds come from the default table
        // variant — the open-in-panel stripe is an inset shadow on the
        // first cell so it can't collide with row borders.
        "transition-none group/row",
        panelSelected &&
          "[&>td:first-child]:shadow-[inset_3px_0_0_var(--color-primary)]"
      )}
    >
      {cells.map((cell) => (
        <TableCell key={cell.id} className="last:py-0 p-1.5">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );
});

export default function CreatorsTableBody({
  columnsLength,
}: CreatorsTableBodyProps) {
  const { table } = useCreatorsTableContext();
  const { selectedCreatorId, serverLoading } = useCreatorsTableState();
  const rows = table.getRowModel().rows;

  // The scroll container is the Table's own wrapper (data-slot=table-container,
  // made the vertical scroller by CreatorsTable) — found from the tbody once
  // it mounts.
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const bodyRef = useCallback((element: HTMLTableSectionElement | null) => {
    setScrollElement(
      element?.closest<HTMLElement>('[data-slot="table-container"]') ?? null
    );
  }, []);

  // Row virtualization: only the rows in (and just around) the viewport
  // exist in the DOM, so a 100-row page costs what a 25-row page used to.
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: OVERSCAN_ROWS,
    initialRect: INITIAL_RECT,
    // Firefox mis-measures <tr> bounding boxes (TanStack Virtual's own
    // table recipe) — let it keep the estimate there.
    measureElement:
      typeof navigator !== "undefined" && navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element.getBoundingClientRect().height
        : undefined,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    // Org-wide, a page in flight dims the rows it is about to replace.
    <TableBody
      ref={bodyRef}
      className={cn(serverLoading && "opacity-60 transition-opacity")}
    >
      {rows.length ? (
        <>
          {paddingTop > 0 && (
            <tr aria-hidden style={{ height: paddingTop }}>
              <td colSpan={columnsLength} className="p-0 border-0" />
            </tr>
          )}
          {virtualRows.map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <TableRowView
                key={row.id}
                row={row}
                cells={row.getVisibleCells()}
                index={virtualRow.index}
                selected={row.getIsSelected()}
                panelSelected={selectedCreatorId === row.original._id}
                measureElement={virtualizer.measureElement}
              />
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden style={{ height: paddingBottom }}>
              <td colSpan={columnsLength} className="p-0 border-0" />
            </tr>
          )}
        </>
      ) : (
        <TableRow>
          <TableCell colSpan={columnsLength} className="h-24 text-center">
            {serverLoading ? (
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <Spinner className="size-4" aria-hidden="true" />
                Loading creators…
              </span>
            ) : (
              "No results."
            )}
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}
